import { infiniteQueryOptions, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  CursorPage,
  DashboardOverviewDto,
  ExecutionMode,
  PaperAccountDto,
  PnlDayDto,
  PositionDto,
  SellResultDto,
  SignalDecision,
  SignalDto,
  TradeDto,
} from '@ultrasnip/contracts';
import { api } from '@/lib/api-client';
import type { PositionDetail } from './trading-cache';

export type PositionStatus = PositionDto['status'];
export type SignalFilter = SignalDecision | 'all';

/**
 * Query keys for everything trading. Positions and dashboard numbers are per execution mode (the
 * API answers for the current one), so the mode is part of their key: switching Paper ⇄ Live
 * never shows the other mode's cache, and live events land only in the list of their own mode.
 */
export const TradingKeys = {
  dashboard: ['dashboard'] as const,
  overview: (mode: ExecutionMode) => ['dashboard', 'overview', mode] as const,
  /** The paper money (not per mode: it is paper's own). */
  paper: ['dashboard', 'paper'] as const,
  pnl: (mode: ExecutionMode, days: number) => ['dashboard', 'pnl', mode, days] as const,
  positions: ['positions'] as const,
  positionList: (status: PositionStatus, mode?: ExecutionMode) =>
    mode ? (['positions', status, mode] as const) : (['positions', status] as const),
  position: (id: string) => ['position', id] as const,
  trades: ['trades'] as const,
  /** One trade looked up by id (the trade sheet's fallback; not under `trades`, so list updates skip it). */
  trade: (id: string) => ['trade', id] as const,
  signals: ['signals'] as const,
  signalList: (filter: SignalFilter) => ['signals', filter] as const,
};

export const POSITIONS_PAGE = 50;
export const TRADES_PAGE = 50;
export const SIGNALS_PAGE = 30;
/** Live arrivals grow the first page; past this the list is refetched so it can't grow forever. */
export const LIVE_PAGE_CAP = 250;

export function overviewQuery(mode: ExecutionMode) {
  return queryOptions({
    queryKey: TradingKeys.overview(mode),
    queryFn: ({ signal }) => api.get<DashboardOverviewDto>('/dashboard/overview', { signal }),
    staleTime: 15_000,
  });
}

export const paperQuery = queryOptions({
  queryKey: TradingKeys.paper,
  queryFn: ({ signal }) => api.get<PaperAccountDto>('/paper', { signal }),
  staleTime: 15_000,
});

/** Adds paper SOL or starts over; the overview (which carries the same figures) refetches. */
export function usePaperMutations() {
  const queryClient = useQueryClient();
  const onSuccess = (account: PaperAccountDto) => {
    queryClient.setQueryData(TradingKeys.paper, account);
    void queryClient.invalidateQueries({ queryKey: TradingKeys.overview('paper') });
  };
  return {
    deposit: useMutation({
      mutationFn: (sol: number) => api.post<PaperAccountDto>('/paper/deposit', { sol }),
      onSuccess,
    }),
    reset: useMutation({
      mutationFn: (sol: number) => api.post<PaperAccountDto>('/paper/reset', { sol }),
      onSuccess,
    }),
  };
}

export function pnlQuery(mode: ExecutionMode, days = 30) {
  return queryOptions({
    queryKey: TradingKeys.pnl(mode, days),
    queryFn: ({ signal }) => api.get<PnlDayDto[]>('/dashboard/pnl', { query: { days }, signal }),
    staleTime: 60_000,
  });
}

export function positionsQuery(status: PositionStatus, mode: ExecutionMode) {
  return infiniteQueryOptions({
    queryKey: TradingKeys.positionList(status, mode),
    queryFn: ({ signal, pageParam }) =>
      api.get<CursorPage<PositionDto>>('/positions', {
        query: { status, limit: POSITIONS_PAGE, cursor: pageParam },
        signal,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    // Marks stream over the socket; a refetch on focus heals anything missed while hidden.
    staleTime: 30_000,
  });
}

export function positionDetailQuery(id: string) {
  return queryOptions({
    queryKey: TradingKeys.position(id),
    queryFn: ({ signal }) => api.get<PositionDetail>(`/positions/${id}`, { signal }),
    staleTime: 15_000,
  });
}

export const tradesQuery = infiniteQueryOptions({
  queryKey: TradingKeys.trades,
  queryFn: ({ signal, pageParam }) =>
    api.get<CursorPage<TradeDto>>('/trades', { query: { limit: TRADES_PAGE, cursor: pageParam }, signal }),
  initialPageParam: null as string | null,
  getNextPageParam: (last) => last.nextCursor,
  // The socket keeps it current (and a reconnect refetches it).
  staleTime: Number.POSITIVE_INFINITY,
});

/** A trade id as the API issues them (a MongoDB ObjectId). */
export function isTradeId(id: string): boolean {
  return /^[a-f0-9]{24}$/i.test(id);
}

/**
 * One trade by id, either execution mode (`GET /api/trades/:id`; 404 when it isn't the user's). The
 * trade sheet uses it when the trade is in neither a loaded history page nor a cached position.
 */
export function tradeQuery(id: string) {
  return queryOptions({
    queryKey: TradingKeys.trade(id),
    queryFn: ({ signal }) => api.get<TradeDto>(`/trades/${encodeURIComponent(id)}`, { signal }),
    staleTime: 15_000,
    // Live events update the lists, not this lookup: poll a trade still in flight until it settles.
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'pending' || status === 'sent' ? 5_000 : false;
    },
  });
}

export function signalsQuery(filter: SignalFilter) {
  return infiniteQueryOptions({
    queryKey: TradingKeys.signalList(filter),
    queryFn: ({ signal, pageParam }) =>
      api.get<CursorPage<SignalDto>>('/signals', {
        query: {
          limit: SIGNALS_PAGE,
          cursor: pageParam,
          decision: filter === 'all' ? undefined : filter,
        },
        signal,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    staleTime: Number.POSITIVE_INFINITY,
  });
}

/** Manual partial or full sell of one position (the engine executes it). */
export function useSellPosition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, percent }: { id: string; percent: number; symbol: string }) =>
      api.post<SellResultDto>(`/positions/${id}/sell`, { percent }),
    onSettled: (_result, _error, { id }) => {
      void queryClient.invalidateQueries({ queryKey: TradingKeys.position(id) });
    },
  });
}

/** Sells every open position of the current mode at emergency slippage. */
export function usePanicSell() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ positions: number }>('/positions/panic'),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: TradingKeys.dashboard });
    },
  });
}
